/* 標準カレンダー */
import React, { useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import RowTemplate from '../atoms/RowTemplate';
import moment from 'moment';
import Messages from '../molecules/Messages';
import { business_day_calendar as config, months } from '../Const';
import ja from '../lang/ja';
import DatePicker from 'react-datepicker';
import Paragraph from '../molecules/Paragraph';
import MaskedInput from 'react-text-mask';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';

interface Props {
  id: string;
  label: string;
  need?: boolean;
  value: Date | null;
  message?: string;
  name?: string;
  placeholder?: string;
  help_msg?: string;
  disabled: boolean;
  looks_inactive?: boolean;
  handleChange: (date: Date | null) => void;
  handleBlur?: VoidFunction;
  readonly?: boolean;
}

const StandardCalendar: React.FC<Props> = ({
  id,
  label,
  need,
  value,
  message,
  name,
  placeholder,
  help_msg,
  disabled,
  looks_inactive,
  handleChange,
  handleBlur,
  readonly,
}) => {
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [enableMask, setEnableMask] = useState(true);
  // 開始日から終了日までの年のコレクションを作成する
  const years: number[] = [];
  const dateFrom = moment(new Date()).add(config.min_date, 'd');
  const dateTo = moment(new Date())
    .add(config.max_year, 'y')
    .subtract('1', 'd');

  for (let i = Number(dateFrom.format('YYYY')); i <= Number(dateTo.format('YYYY')); i++) {
    years.push(i);
  }

  // 配列を反転させる
  years.reverse();

  /**
   * カレンダーアイコン押下時の処理
   * @return void
   */
  const handleCalendarIconClick = (): void => {
    // Note:アイコン押下時のみカレンダー表示
    //      カレンダークローズ時にフラグOFF
    setOpenDatePicker(true);
    const obj = document.getElementById(id);
    if (obj !== null) {
      obj.click();
    }
  };

  /**
   * カレンダークローズ時の処理
   * @return void
   */
  const handleCalendarClose = (): void => {
    (typeof handleBlur !== 'undefined') && handleBlur();
    setOpenDatePicker(false);
  };

  /**
   * DatePickerの上位要素のフォーカスアウト時の処理
   * @return void
   */
  const handleBlurTopLevelElement = (): void => {
    if (openDatePicker) {
      // カレンダー表示時のhandleBlurの２回発火抑止
      return;
    }

    const obj = document.getElementById(id) as HTMLInputElement;
    const inputDate = obj.value;
    if (inputDate !== '' &&
        inputDate.match(/^[0-9]{4}\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/) === null) {
      // Note:無効な日付(入力が半端な状態)の場合は、
      //      入力Maskが有効になっていると、無効な日付が画面に残ってしまう
      //      そのため、入力Maskを無効化し、内部値もクリアさせる
      setEnableMask(false);
      handleChange(null);
    }

    (typeof handleBlur !== 'undefined') && handleBlur();
  };

  /**
   * 活性状態の時に出力するJSX.ELement
   * @returns JSX.Element
   */
  const enabledJsxElement = () => {
    return (
      <div className="standard-calendar" onBlur={handleBlurTopLevelElement}>
        <Row>
          <Col xs={9} sm={9} md={9} lg={9} xl={9}>
            <DatePicker
              id={id}
              className={`form-control ${message && 'textarea-error'}`}
              disabled={disabled || readonly}
              locale={ja}
              onChange={handleChange}
              selected={value ?? null}
              placeholderText={placeholder}
              name={name}
              dateFormat="yyyy/MM/dd"
              showPopperArrow={false}
              autoComplete="off"
              onCalendarClose={handleCalendarClose}
              onInputClick={() => {
                setEnableMask(true)
              }}
              open={openDatePicker}
              customInput={
                <MaskedInput
                  pipe={createAutoCorrectedDatePipe('yyyy/mm/dd')}
                  mask={enableMask ? [/\d/, /\d/, /\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/] : []}
                  guide
                  type='tel'
                />
              }
              renderCustomHeader={({
                date,
                changeYear,
                changeMonth,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => (
                <div className="react-datepicker-render-custom-header">
                  <button
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                    className="navigation--previous"
                  >
                    <img src="/img/ico_left_arrow.png" />
                  </button>
                  <select
                    value={date.getFullYear()}
                    onChange={({ target: { value } }) => changeYear(Number(value))}
                  >
                    {years.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <span>年</span>
                  <select
                    value={months[date.getMonth()]}
                    onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
                  >
                    {months.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                    className="navigation--next"
                  >
                    <img src="/img/ico_right_arrow.png" />
                  </button>
                </div>
              )}
            />
          </Col>
          {!readonly && (
            <Col xs={3} sm={3} md={3} lg={3} xl={3}>
              <button className="calendar-open" onClick={handleCalendarIconClick}>
                <img src="/img/ico_009_pc.png" />
              </button>
            </Col>
          )}
        </Row>
        <Messages help_msg={help_msg} valid_msg={message} />
      </div>
    );
  };

  /**
   * ラベル表示時のJSX.Elementを返却する
   * @returns JSX.Element
   */
  const returnLabelJsx = () => {
    if (value === null) {
      return <></>;
    }

    const date = moment(value).format('YYYY/MM/DD');
    return <Paragraph label={date} />;
  };

  return (
    <RowTemplate
      label={label}
      need={need}
      disabled={disabled}
      looks_inactive={looks_inactive}
      content={disabled || looks_inactive ? returnLabelJsx() : enabledJsxElement()}
    />
  );
};

export default StandardCalendar;
